/**
 * Map support (spec §9 FREE-001…003, ROUTE-008, ROUTE-012).
 * Pure Web-Mercator math, basemap layer definitions, pin states, and a simple
 * suggested stop order. Full RouteIQ optimisation (likelihood, time windows,
 * traffic) is Phase 6; this ordering is deliberately transparent.
 */
import { distanceFeet, type LatLng } from './geo.ts';

export const TILE_SIZE = 256;
const MAX_LAT = 85.05112878;

/** Pixel position in the whole world at a zoom level. */
export function lngLatToWorld(lng: number, lat: number, zoom: number): { x: number; y: number } {
  const scale = TILE_SIZE * 2 ** zoom;
  const clamped = Math.max(-MAX_LAT, Math.min(MAX_LAT, lat));
  const s = Math.sin((clamped * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * scale,
  };
}

export function worldToLngLat(x: number, y: number, zoom: number): LatLng {
  const scale = TILE_SIZE * 2 ** zoom;
  const lng = (x / scale) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / scale;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { lat, lng };
}

/** Center and integer zoom that fit all points inside a viewport. */
export function fitBounds(points: LatLng[], width: number, height: number, opts: { padding?: number; maxZoom?: number; minZoom?: number } = {}): { center: LatLng; zoom: number } {
  const pad = opts.padding ?? 40;
  const maxZ = opts.maxZoom ?? 17;
  const minZ = opts.minZoom ?? 3;
  if (!points.length) return { center: { lat: 34.0, lng: -81.03 }, zoom: 8 };
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const sw = { lat: Math.min(...lats), lng: Math.min(...lngs) };
  const ne = { lat: Math.max(...lats), lng: Math.max(...lngs) };
  let zoom = maxZ;
  for (; zoom > minZ; zoom--) {
    const a = lngLatToWorld(sw.lng, sw.lat, zoom);
    const b = lngLatToWorld(ne.lng, ne.lat, zoom);
    if (Math.abs(b.x - a.x) <= width - pad * 2 && Math.abs(a.y - b.y) <= height - pad * 2) break;
  }
  const a = lngLatToWorld(sw.lng, sw.lat, zoom);
  const b = lngLatToWorld(ne.lng, ne.lat, zoom);
  return { center: worldToLngLat((a.x + b.x) / 2, (a.y + b.y) / 2, zoom), zoom };
}

// ─── Basemap layers ──────────────────────────────────────────────────────────

export type MapLayerId = 'street' | 'satellite' | 'hybrid';

export interface MapLayer {
  id: MapLayerId;
  label: string;
  /** Template with {z} {x} {y}. */
  url: string;
  maxZoom: number;
  attribution: string;
  /** Tile images are 256 px (default) or 512 px. */
  tileSize?: 256 | 512;
  /** A second, transparent tile layer drawn on top (e.g. labels over imagery). */
  overlayUrl?: string;
  overlayTileSize?: 256 | 512;
  provider?: 'free' | 'esri';
}

/**
 * Default basemaps. Only tile images are requested — no case data or addresses
 * are sent. Agencies can swap in county GIS or commercial imagery later.
 */
export const MAP_LAYERS: Record<MapLayerId, MapLayer> = {
  street: {
    id: 'street',
    label: 'Street',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    maxZoom: 19,
    attribution: '© OpenStreetMap contributors',
  },
  satellite: {
    id: 'satellite',
    label: 'Satellite',
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}',
    maxZoom: 16,
    attribution: 'Imagery: USGS The National Map',
  },
  hybrid: {
    id: 'hybrid',
    label: 'Hybrid',
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryTopo/MapServer/tile/{z}/{y}/{x}',
    maxZoom: 16,
    attribution: 'Imagery & labels: USGS The National Map',
  },
};

export function tileUrl(layer: MapLayer | string, z: number, x: number, y: number): string {
  const url = typeof layer === 'string' ? layer : layer.url;
  return url.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
}

export const ESRI_STATIC_TILES = 'https://static-map-tiles-api.arcgis.com/arcgis/rest/services/static-basemap-tiles-service/v1';
export const ESRI_IMAGERY_TILES = 'https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile';
/** Hosts the browser loads Esri tiles from (for the Content-Security-Policy). */
export const ESRI_TILE_HOSTS = ['https://static-map-tiles-api.arcgis.com', 'https://ibasemaps-api.arcgis.com'];

/**
 * Esri basemaps (ArcGIS Location Platform) when the agency has an Esri API key: Esri Navigation streets,
 * World Imagery, and imagery with Esri's road and place labels on top. Only tile images are requested.
 */
export function esriLayers(apiKey: string): Record<MapLayerId, MapLayer> {
  const t = `?token=${encodeURIComponent(apiKey)}`;
  const imagery = `${ESRI_IMAGERY_TILES}/{z}/{y}/{x}${t}`;
  return {
    street: {
      id: 'street', label: 'Street', provider: 'esri', tileSize: 512, maxZoom: 19,
      url: `${ESRI_STATIC_TILES}/arcgis/navigation/static/tile/{z}/{y}/{x}${t}`,
      attribution: 'Esri, HERE, Garmin, FAO, NOAA, USGS, © OpenStreetMap contributors',
    },
    satellite: { id: 'satellite', label: 'Satellite', provider: 'esri', maxZoom: 19, url: imagery, attribution: 'Esri, Maxar, Earthstar Geographics, and the GIS User Community' },
    hybrid: {
      id: 'hybrid', label: 'Hybrid', provider: 'esri', maxZoom: 19, url: imagery,
      overlayUrl: `${ESRI_STATIC_TILES}/arcgis/imagery/labels/static/tile/{z}/{y}/{x}${t}`, overlayTileSize: 512,
      attribution: 'Esri, Maxar, Earthstar Geographics, HERE, Garmin, and the GIS User Community',
    },
  };
}

/** The basemaps to use: Esri when a key is set, otherwise the free OpenStreetMap / USGS maps. */
export function basemapsFor(maps: { provider?: string | null; esriApiKey?: string | null } | null | undefined): Record<MapLayerId, MapLayer> {
  return maps?.provider === 'esri' && maps.esriApiKey ? esriLayers(maps.esriApiKey) : MAP_LAYERS;
}

// ─── Pins (FREE-002) ─────────────────────────────────────────────────────────

/**
 * green / yellow / red = ServeIQ likelihood (Phase 5). Until a likelihood
 * exists, eligible papers are "nsh" (no service history — SIQ-008), which is
 * a distinct state and not treated as low probability.
 * gray = the paper exists but an attempt is not eligible right now.
 */
export type PinState = 'green' | 'yellow' | 'red' | 'nsh' | 'gray';

export const PIN_COLORS: Record<PinState, string> = {
  green: '#1f9d55',
  yellow: '#e0a100',
  red: '#c9362b',
  nsh: '#2f6fd6',
  gray: '#8b95a5',
};

export const PIN_LABELS: Record<PinState, string> = {
  green: 'High likelihood now',
  yellow: 'Moderate / uncertain',
  red: 'Low likelihood now',
  nsh: 'Eligible — no service history yet',
  gray: 'Not eligible to attempt now',
};

export function pinState(input: { eligibleNow: boolean; likelihood?: number | null }): PinState {
  if (!input.eligibleNow) return 'gray';
  const l = input.likelihood;
  if (l == null) return 'nsh';
  if (l >= 0.6) return 'green';
  if (l >= 0.3) return 'yellow';
  return 'red';
}

// ─── Suggested stop order (ROUTE-008) ────────────────────────────────────────

export interface RouteStopInput {
  id: string;
  lat: number;
  lng: number;
  /** Normalized address key — same address ⇒ one travel stop. */
  addressKey: string;
  /** Higher = more urgent; used only to break near-ties and pick a start. */
  priority?: number;
}

export interface RouteStop {
  addressKey: string;
  lat: number;
  lng: number;
  paperIds: string[];
  /** Miles from the previous stop (or from the origin). */
  legMiles: number;
}

export function addressKey(a: { line1?: string | null; line2?: string | null; city?: string | null; zip?: string | null }): string {
  return [a.line1, a.line2, a.city, a.zip]
    .map((x) => (x ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim())
    .join('|');
}

const FEET_PER_MILE = 5280;

/**
 * Greedy nearest-next ordering from the current position (or from the most
 * urgent stop when no position is known). Stops at the same address merge.
 * Urgent papers are always included — ROUTE-004 requires them to be scheduled
 * that day, not necessarily first.
 */
export function suggestStopOrder(stops: RouteStopInput[], origin: LatLng | null): { stops: RouteStop[]; totalMiles: number } {
  const groups = new Map<string, RouteStop & { priority: number }>();
  for (const s of stops) {
    const g = groups.get(s.addressKey);
    if (g) {
      g.paperIds.push(s.id);
      g.priority = Math.max(g.priority, s.priority ?? 0);
    } else {
      groups.set(s.addressKey, { addressKey: s.addressKey, lat: s.lat, lng: s.lng, paperIds: [s.id], legMiles: 0, priority: s.priority ?? 0 });
    }
  }
  const remaining = [...groups.values()];
  const out: RouteStop[] = [];
  let here: LatLng | null = origin;
  if (!here && remaining.length) {
    remaining.sort((a, b) => b.priority - a.priority);
    here = { lat: remaining[0]!.lat, lng: remaining[0]!.lng };
  }
  let total = 0;
  while (remaining.length) {
    let bestI = 0;
    let bestScore = Infinity;
    remaining.forEach((g, i) => {
      const d = distanceFeet(here!, g);
      // A small urgency bonus (~0.3 mi per priority point) breaks near-ties toward urgent stops.
      const score = d - g.priority * 0.3 * FEET_PER_MILE;
      if (score < bestScore) {
        bestScore = score;
        bestI = i;
      }
    });
    const g = remaining.splice(bestI, 1)[0]!;
    const miles = distanceFeet(here!, g) / FEET_PER_MILE;
    total += miles;
    out.push({ addressKey: g.addressKey, lat: g.lat, lng: g.lng, paperIds: g.paperIds, legMiles: Math.round(miles * 10) / 10 });
    here = g;
  }
  return { stops: out, totalMiles: Math.round(total * 10) / 10 };
}

/**
 * Plans a driving order from a start point to an optional finish point (straight-line miles).
 * Stops at the same address merge. Nearest-next from the start, then 2-opt clean-up that removes
 * crossings while keeping the start fixed and, when given, ending at the finish.
 */
export function planRoute(stops: RouteStopInput[], start: LatLng, finish: LatLng | null): { stops: RouteStop[]; totalMiles: number; finishMiles: number } {
  const groups = new Map<string, RouteStop>();
  for (const s of stops) {
    const g = groups.get(s.addressKey);
    if (g) g.paperIds.push(s.id);
    else groups.set(s.addressKey, { addressKey: s.addressKey, lat: s.lat, lng: s.lng, paperIds: [s.id], legMiles: 0 });
  }
  const remaining = [...groups.values()];
  const path: RouteStop[] = [];
  let here: LatLng = start;
  while (remaining.length) {
    let bi = 0;
    let bd = Infinity;
    remaining.forEach((g, i) => {
      const d = distanceFeet(here, g);
      if (d < bd) {
        bd = d;
        bi = i;
      }
    });
    here = remaining.splice(bi, 1)[0]!;
    path.push(here as RouteStop);
  }
  // 2-opt over [start, ...path, finish?]: reverse segments while it shortens the trip.
  const pts: LatLng[] = [start, ...path, ...(finish ? [finish] : [])];
  const d = (a: LatLng, b: LatLng) => distanceFeet(a, b);
  const lastMovable = finish ? pts.length - 2 : pts.length - 1;
  let improved = true;
  for (let pass = 0; improved && pass < 50; pass++) {
    improved = false;
    for (let i = 1; i < lastMovable; i++) {
      for (let k = i + 1; k <= lastMovable; k++) {
        const before = d(pts[i - 1]!, pts[i]!) + (k + 1 < pts.length ? d(pts[k]!, pts[k + 1]!) : 0);
        const after = d(pts[i - 1]!, pts[k]!) + (k + 1 < pts.length ? d(pts[i]!, pts[k + 1]!) : 0);
        // Only real savings (> ~50 ft): ties keep the nearest-first order, so a paper at the start stays first.
        if (after + 50 < before) {
          const seg = pts.slice(i, k + 1).reverse();
          pts.splice(i, seg.length, ...seg);
          improved = true;
        }
      }
    }
  }
  const ordered = pts.slice(1, finish ? -1 : undefined) as RouteStop[];
  let prev: LatLng = start;
  let total = 0;
  const out = ordered.map((g) => {
    const miles = distanceFeet(prev, g) / FEET_PER_MILE;
    total += miles;
    prev = g;
    return { addressKey: g.addressKey, lat: g.lat, lng: g.lng, paperIds: g.paperIds, legMiles: Math.round(miles * 10) / 10 };
  });
  const finishMiles = finish ? distanceFeet(prev, finish) / FEET_PER_MILE : 0;
  total += finishMiles;
  return { stops: out, totalMiles: Math.round(total * 10) / 10, finishMiles: Math.round(finishMiles * 10) / 10 };
}

/** Is the point inside the outline (ray casting; lng = x, lat = y)? */
export function pointInPolygon(pt: { lat: number; lng: number }, outline: { lat: number; lng: number }[]): boolean {
  let inside = false;
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    const a = outline[i]!;
    const b = outline[j]!;
    if (a.lat > pt.lat !== b.lat > pt.lat && pt.lng < ((b.lng - a.lng) * (pt.lat - a.lat)) / (b.lat - a.lat) + a.lng) inside = !inside;
  }
  return inside;
}
